import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaBars, FaTimes } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  // Helper function to check if a path is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className="bg-white relative md:sticky top-0 z-50 shadow-md">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-between md:justify-start mb-4 md:mb-0"
          >
            <div className="flex items-center">
              <motion.img 
                src={logo}  
                alt="JDP Finance Logo" 
                className="h-20 md:h-24 object-contain"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </div>
            
            {/* Mobile Menu Toggle */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-blue-900 hover:text-green-600 transition-colors duration-300"
              >
                {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
              </button>
            </div>
          </motion.div>

          {/* Main Navigation - Desktop */}
          <motion.nav 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="hidden md:flex items-center justify-center md:justify-end space-x-6 uppercase font-medium"
          >
            <a href="/" className={`${isActive('/') ? 'text-blue-900' : 'text-gray-700'} hover:text-green-600 transition-colors duration-300 relative group`}>
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-500 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="/costs" className={`${isActive('/costs') ? 'text-blue-900' : 'text-gray-700'} hover:text-green-600 transition-colors duration-300 relative group`}>
              Costs
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-500 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="/apply-now" className={`${isActive('/apply-now') ? 'text-blue-900' : 'text-gray-700'} hover:text-green-600 transition-colors duration-300 relative group`}>
              Apply Now
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-500 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="/contact-us" className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors duration-300">
              Contact Us
            </a>
          </motion.nav>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden w-full"
            >
              <div className="flex flex-col space-y-4 py-4">
                <a href="/" className={`${isActive('/') ? 'text-blue-900' : 'text-gray-700'} hover:text-green-600 transition-colors duration-300 font-medium`}>
                  Home
                </a>
                <a href="/costs" className={`${isActive('/costs') ? 'text-blue-900' : 'text-gray-700'} hover:text-green-600 transition-colors duration-300 font-medium`}>
                  Costs
                </a>
                <a href="/apply-now" className={`${isActive('/apply-now') ? 'text-blue-900' : 'text-gray-700'} hover:text-green-600 transition-colors duration-300 font-medium`}>
                  Apply Now
                </a>
                <a href="/contact-us" className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-center">
                  Contact Us
                </a>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* Contact Information and License Text - Desktop */}
        <div className="hidden md:block">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex justify-end mt-2 text-gray-700 items-center"
          >
            <span className="mr-2">Need help?</span>
            <a href="tel:0425625605" className="flex items-center text-green-600 font-medium hover:text-blue-900 transition-colors duration-300 mx-2">
              <FaPhone className="mr-1" size={14} /> 0425 625 605
            </a> 
            <span className="mx-1 text-gray-500">|</span>
            <a href="mailto:contact@jdpfinance.com.au" className="flex items-center text-green-600 hover:text-blue-900 transition-colors duration-300 mx-2">
              <FaEnvelope className="mr-1" size={14} /> contact@jdpfinance.com.au
            </a>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-right text-xs text-gray-600 mt-1 italic"
          >
            {/* JDP Finance Pty Ltd ACN  686 530 361, facilitating loans provided by Strawberry Loans Pty Ltd ACN 621 474 655 Australian Credit Licence Number 519253. AFCA ‘s membership number: 115010 */}
          </motion.div>
        </div>

        {/* Contact Information and License Text - Mobile */}
        <div className="md:hidden">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex flex-wrap justify-center mt-2 text-gray-700 items-center"
          >
            <span className="mr-2">Need help?</span>
            <a href="tel:0425625605" className="flex items-center text-green-600 font-medium hover:text-blue-900 transition-colors duration-300 mx-2">
              <FaPhone className="mr-1" size={14} /> 0425 625 605
            </a> 
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex flex-wrap justify-center mt-1 text-gray-700 items-center"
          >
            <a href="mailto:contact@jdpfinance.com.au" className="flex items-center text-green-600 hover:text-blue-900 transition-colors duration-300">
              <FaEnvelope className="mr-1" size={14} /> contact@jdpfinance.com.au
            </a>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-center text-xs text-gray-600 mt-2 italic px-2 leading-tight"
          >
            {/* JDP Finance Pty Ltd ACN 686 530 361, facilitating loans provided by Strawberry Loans Pty Ltd ACN 621 474 655 Australian Credit Licence Number 519253. AFCA’s membership number: 115010 */}
          </motion.div>
        </div>
      </div>
    </header>
  );
};

export default Header; 